import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { DownloadSimple, Trash } from "@phosphor-icons/react";

const STATUSES = ["new", "in_progress", "won", "lost", "archived"];

export default function LeadsAdmin() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [source, setSource] = useState("");

  const load = async () => {
    const r = await api.get("/leads", source ? { params: { source } } : {});
    setItems(r.data);
  };

  useEffect(() => { load(); }, [source]);

  const update = async (id, status) => {
    try { await api.patch(`/leads/${id}`, { status }); toast.success("Updated"); load(); }
    catch { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/leads/${id}`); load();
  };

  const exportCsv = () => {
    const token = localStorage.getItem("maxine_admin_token");
    const url = `${API}/leads/export.csv`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = "leads.csv";
        a.click();
      });
  };

  const filtered = items.filter((l) =>
    !filter || l.name?.toLowerCase().includes(filter.toLowerCase()) ||
    l.email?.toLowerCase().includes(filter.toLowerCase()) ||
    l.message?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div data-testid="admin-leads" className="p-8 md:p-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display font-black text-3xl md:text-4xl">Leads inbox</h1>
          <p className="mt-2 text-neutral-600">Contact form + career applications.</p>
        </div>
        <button onClick={exportCsv} data-testid="leads-export" className="btn-ghost text-sm py-2.5 px-4">
          <DownloadSimple size={16} weight="bold" /> Export CSV
        </button>
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <input
          data-testid="leads-filter"
          value={filter} onChange={(e) => setFilter(e.target.value)}
          placeholder="Search name, email, message…"
          className="flex-1 min-w-[240px] border border-[color:var(--line)] px-4 py-2.5 outline-none focus:border-[color:var(--brand)]"
        />
        <select value={source} onChange={(e) => setSource(e.target.value)} data-testid="leads-source" className="border border-[color:var(--line)] px-4 py-2.5">
          <option value="">All sources</option>
          <option value="contact">Contact</option>
          <option value="career">Career</option>
          <option value="quote">Quote</option>
        </select>
      </div>

      <div className="mt-6 bg-white border border-[color:var(--line)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left">
            <tr className="border-b border-[color:var(--line)] font-mono text-[10px] uppercase tracking-widest text-neutral-500">
              <th className="p-4">When</th>
              <th className="p-4">Name / Email</th>
              <th className="p-4">Source</th>
              <th className="p-4">Message</th>
              <th className="p-4">Status</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-[color:var(--line)] align-top">
                <td className="p-4 font-mono text-xs whitespace-nowrap">{String(l.created_at).slice(0, 16).replace("T", " ")}</td>
                <td className="p-4">
                  <div className="font-semibold">{l.name}</div>
                  <div className="text-xs text-neutral-500">{l.email}</div>
                  {l.phone && <div className="text-xs text-neutral-500">{l.phone}</div>}
                  {l.company && <div className="text-xs text-neutral-500">{l.company}</div>}
                </td>
                <td className="p-4"><span className="font-mono text-[10px] uppercase tracking-widest bg-neutral-100 px-2 py-1">{l.source}</span></td>
                <td className="p-4 text-neutral-700 max-w-md">{l.message}{l.role && <div className="text-xs text-neutral-500 mt-1">Role: {l.role}</div>}</td>
                <td className="p-4">
                  <select
                    value={l.status || "new"}
                    onChange={(e) => update(l.id, e.target.value)}
                    data-testid={`lead-status-${l.id}`}
                    className="border border-[color:var(--line)] px-2 py-1 text-xs"
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                </td>
                <td className="p-4">
                  <button onClick={() => del(l.id)} data-testid={`lead-del-${l.id}`} className="text-neutral-400 hover:text-[color:var(--brand)] transition-colors">
                    <Trash size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-neutral-500">No leads match your filter.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
